import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { SrOrderStatus } from '@plato/data/starrocks/types';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { mergeFilterOptions } from '@plato/ui-legacy/components/filters/lib/merge-filter-options';
import type { VerticalFilterOption } from '@plato/ui-legacy/components/filters/types/filter-types';
import { keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { safeJoin } from '../../../utils/string-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { VerticalFilterTemplate } from '../../filters/templates/vertical-filter-template.tsx';
import {
    defaultQuoteOrderFilters,
    useQuoteOrderListFilters,
    type QuoteOrderQueryFilters,
} from '../hooks/use-quote-order-list-filters.tsx';

// Statuses follow the document's own lifecycle rather than count or alphabet, and the two document
// types have different lifecycles. Statuses missing from these lists trail behind, count-desc.
const orderStatusSortOrder = [
    SrOrderStatus.OPEN,
    SrOrderStatus.OPEN_IN_PICKING,
    SrOrderStatus.PARTIALLY_PICKED,
    SrOrderStatus.PICKED,
    SrOrderStatus.PARTIALLY_BILLED,
    SrOrderStatus.PARTIALLY_SHIPPED,
    SrOrderStatus.SHIPPED,
    SrOrderStatus.BILLED,
    SrOrderStatus.CANCELLED,
    SrOrderStatus.OTHER,
    SrOrderStatus.UNKNOWN,
];

const quoteStatusSortOrder = [
    SrOrderStatus.OPEN,
    SrOrderStatus.PARTIALLY_RECEIVED,
    SrOrderStatus.RECEIVED,
    SrOrderStatus.LOST,
    SrOrderStatus.CANCELLED,
    SrOrderStatus.OTHER,
    SrOrderStatus.UNKNOWN,
];

type Props = {
    orderType: SrSalesOrderType;
    quickFilters?: QuickFilter<QuoteOrderQueryFilters>[];
    canGetFilters?: boolean;
};

export function QuoteOrderListFilters({ orderType, quickFilters, canGetFilters }: Props) {
    const { t } = useTranslation('quotes-orders-list');
    const getStatusLabel = (status: SrOrderStatus): string => {
        switch (status) {
            case SrOrderStatus.OPEN:
                return t('OPEN', { ns: 'order-status' });
            case SrOrderStatus.PARTIALLY_BILLED:
                return t('PARTIALLY_BILLED', { ns: 'order-status' });
            case SrOrderStatus.BILLED:
                return t('BILLED', { ns: 'order-status' });
            case SrOrderStatus.OPEN_IN_PICKING:
                return t('OPEN_IN_PICKING', { ns: 'order-status' });
            case SrOrderStatus.PROCESSED_INTERNALLY:
                return t('PROCESSED_INTERNALLY', { ns: 'order-status' });
            case SrOrderStatus.CANCELLED:
                return t('CANCELLED', { ns: 'order-status' });
            case SrOrderStatus.PARTIALLY_RECEIVED:
                return t('PARTIALLY_RECEIVED', { ns: 'order-status' });
            case SrOrderStatus.RECEIVED:
                return t('RECEIVED', { ns: 'order-status' });
            case SrOrderStatus.LOST:
                return t('LOST', { ns: 'order-status' });
            case SrOrderStatus.REVISED:
                return t('REVISED', { ns: 'order-status' });
            case SrOrderStatus.COMPLETED:
                return t('COMPLETED', { ns: 'order-status' });
            case SrOrderStatus.UNKNOWN:
                return t('UNKNOWN', { ns: 'order-status' });
            case SrOrderStatus.RELEASED_FOR_DELIVERY:
                return t('RELEASED_FOR_DELIVERY', { ns: 'order-status' });
            case SrOrderStatus.RETURNED:
                return t('RETURNED', { ns: 'order-status' });
            case SrOrderStatus.SHIPPED:
                return t('SHIPPED', { ns: 'order-status' });
            case SrOrderStatus.PARTIALLY_PICKED:
                return t('PARTIALLY_PICKED', { ns: 'order-status' });
            case SrOrderStatus.PICKED:
                return t('PICKED', { ns: 'order-status' });
            case SrOrderStatus.PARTIALLY_SHIPPED:
                return t('PARTIALLY_SHIPPED', { ns: 'order-status' });
            case SrOrderStatus.PENDING:
                return t('PENDING', { ns: 'order-status' });
            case SrOrderStatus.OTHER:
                return t('OTHER', { ns: 'order-status' });
        }
    };
    const sendEvent = useAnalytics();

    const { canReadGrossMargin } = useAppConfig();

    const [appliedFilters, setAppliedFilters] = useQuoteOrderListFilters(orderType);
    const [modalFilters, setModalFilters] = useState<QuoteOrderQueryFilters>(appliedFilters);

    const filterOptionsInput = {
        ...modalFilters,
        searchText: appliedFilters.searchText ?? '',
        orderTypes: [orderType] as SrSalesOrderType[],
        orderStatus: modalFilters.orderStatus as SrOrderStatus[],
        entryDateRange: modalFilters.entryDateRange,
    };

    const { data: filterOptions, isLoading: isLoadingFilterOptions } =
        api.platoUser.getManyQuotesOrders.getFilterOptions.useQuery(filterOptionsInput, {
            placeholderData: keepPreviousData,
            enabled: canGetFilters ?? true,
        });

    const { data: filterOptionsWithoutSearch } = api.platoUser.getManyQuotesOrders.getFilterOptions.useQuery(
        { ...filterOptionsInput, searchText: '' },
        { placeholderData: keepPreviousData, enabled: canGetFilters ?? true },
    );

    const { data: filterAggregates, isLoading: isLoadingCount } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery({
            ...modalFilters,
            orderTypes: [orderType],
            orderStatus: modalFilters.orderStatus as SrOrderStatus[],
            entryDateRange: modalFilters.entryDateRange,
        });
    const count = filterAggregates?.count;

    // Ordered by how many distinct users actually apply each filter (PostHog, 180d, orders and
    // quotes combined).
    const filtersConfig: VerticalFilterOption<QuoteOrderQueryFilters>[] = [
        {
            id: 'orderStatus',
            label: t('filter_menu_title__status'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.statuses,
                filterOptionsWithoutSearch?.statuses,
                ({ status, count }) => ({
                    id: status,
                    label: getStatusLabel(status),
                    value: status,
                    count,
                }),
                appliedFilters.orderStatus,
                { sortOrder: orderType === 'ORDER' ? orderStatusSortOrder : quoteStatusSortOrder },
            ),
        },
        {
            id: 'entryDateRange',
            label:
                orderType === 'ORDER'
                    ? t('filter_menu_title__order_date')
                    : t('filter_menu_title__quote_date'),
            type: 'dateRange',
        },
        {
            id: 'clerkIds',
            label: t('filter_menu_title__clerk'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.clerks,
                filterOptionsWithoutSearch?.clerks,
                (o) => ({
                    id: o.clerk_id,
                    label: safeJoin([o.clerk_id, o.clerk_name], ': '),
                    value: o.clerk_id,
                    count: o.count,
                }),
                appliedFilters.clerkIds,
            ),
        },
        {
            id: 'salesRepIds',
            label: t('filter_menu_title__sales_rep'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.salesReps,
                filterOptionsWithoutSearch?.salesReps,
                (o) => ({ id: o.rep_id, label: `${o.rep_id}: ${o.name}`, value: o.rep_id, count: o.count }),
                appliedFilters.salesRepIds,
            ),
        },
        {
            id: 'totalAmountNet',
            label: t('filter_menu_title__total_amount_net'),
            type: 'priceRange',
        },
        {
            id: 'documentTypes',
            label:
                orderType === 'ORDER'
                    ? t('filter_menu_title__order_type')
                    : t('filter_menu_title__quote_type'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.documentTypes,
                filterOptionsWithoutSearch?.documentTypes,
                (o) => ({
                    id: o.sales_order_type_id,
                    label: o.sales_order_type_name,
                    value: o.sales_order_type_id,
                    count: o.count,
                }),
                appliedFilters.documentTypes,
            ),
        },
        // only include totalMargin filter when the user has permission to read gross margin
        ...(canReadGrossMargin
            ? ([
                  {
                      id: 'totalMargin' as const,
                      label: t('filter_menu_title__total_margin'),
                      type: 'priceRange',
                  },
              ] as VerticalFilterOption<QuoteOrderQueryFilters>[])
            : []),
        {
            id: 'storageLocationIds',
            label: t('filter_menu_title__location'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.storageLocations,
                filterOptionsWithoutSearch?.storageLocations,
                (o) => ({
                    id: o.storage_location_id,
                    label: o.storage_location_name,
                    value: o.storage_location_id,
                    count: o.count,
                }),
                appliedFilters.storageLocationIds,
            ),
        },
    ];

    const handleApplyFilters = (filters: QuoteOrderQueryFilters) => {
        setAppliedFilters(filters);

        sendEvent('quote_order_list_filter_used', getUsedFilterEventPayload(filters));
    };

    return (
        <VerticalFilterTemplate<QuoteOrderQueryFilters>
            aclSubject="QuoteOrder"
            viewKey={orderType === 'QUOTE' ? 'quotes.list' : 'orders.list'}
            searchFilterKey="searchText"
            searchPlaceholder={
                orderType === 'QUOTE' ? t('search_placeholder__quotes') : t('search_placeholder__orders')
            }
            filters={filtersConfig}
            onFiltersChange={setModalFilters}
            itemCount={count ?? 0}
            isLoading={isLoadingFilterOptions || isLoadingCount}
            defaultFilters={defaultQuoteOrderFilters}
            appliedFilters={appliedFilters}
            onApplyFilters={handleApplyFilters}
            quickFilters={quickFilters}
        />
    );
}
